import { useCallback, useEffect, useMemo, useState } from 'react';
import { Cable, Clock, MoreVertical, Package, Plus, RefreshCw, Settings, Shield, Tag } from 'lucide-react';
import { Input } from '../components/ui/Input';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { apiClient, type ClusterBrokerRegistration } from '../lib/api-client';
import { PageHeader } from '../components/ui/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { NoGroupsMessage } from '../components/NoGroupsMessage';

function statusVariant(status: string): 'success' | 'warning' | 'destructive' | 'secondary' | 'outline' {
  if (status === 'online') return 'success';
  if (status === 'pending' || status === 'enrollment-required' || status === 'provisioning' || status === 'starting') return 'warning';
  if (status === 'degraded' || status === 'offline-unexpected') return 'destructive';
  if (status === 'revoked' || status === 'stopped') return 'secondary';
  return 'outline';
}

function statusLabel(status: string): string {
  return status
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function profileLabel(profile: string): string {
  if (profile === 'openshift-readonly') return 'OpenShift read-only';
  if (profile === 'k8s-readonly') return 'Kubernetes read-only';
  if (profile === 'developer-admin') return 'Developer admin';
  return profile;
}

function classificationLabel(value: string): string {
  if (value === 'production') return 'Production';
  if (value === 'development') return 'Development';
  return value;
}

function formatWhen(value?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatSeen(value?: string): string {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d ago`;
  return date.toLocaleDateString();
}

const PROFILES = ['openshift-readonly', 'k8s-readonly', 'developer-admin'] as const;

function fieldLabel(text: string) {
  return <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{text}</label>;
}

export function ClusterBrokers() {
  const { user, selectedTenants, accessibleTenants } = useAuth();
  const { isAllowed: canView, isLoading: permissionLoading } = usePermission('openshift_clusters', 'read');
  const { isAllowed: canManage } = usePermission('openshift_clusters', 'manage_server');
  const [rows, setRows] = useState<ClusterBrokerRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ id: string; action: 'revoke' | 'rotate'; label: string } | null>(null);
  const [token, setToken] = useState<{ id: string; cluster: string; value?: string; expiresAt?: string } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    tenant: '',
    environment: '',
    clusterName: '',
    profile: 'openshift-readonly',
    clusterClassification: 'production',
  });

  const hasNoGroups = user && (!user.groups || user.groups.length === 0);

  const load = useCallback(async () => {
    if (!canView) return;
    try {
      const result = await apiClient.listClusterBrokers();
      setRows(result.registrations || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cluster brokers');
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    void load();
    const timer = window.setInterval(() => void load(), 20000);
    return () => window.clearInterval(timer);
  }, [canView, load]);

  const visible = useMemo(() => {
    const selected = new Set((selectedTenants || []).map((tenant) => tenant.toLowerCase()));
    if (selected.size === 0) return rows;
    return rows.filter((row) => selected.has(row.tenant.toLowerCase()));
  }, [rows, selectedTenants]);

  async function runConfirmed() {
    if (!confirm) return;
    setBusyId(confirm.id);
    setError(null);
    try {
      if (confirm.action === 'revoke') {
        await apiClient.revokeClusterBroker(confirm.id);
      } else {
        const rotated = await apiClient.rotateClusterBroker(confirm.id);
        if (rotated.bootstrapToken) {
          setToken({
            id: rotated.id,
            cluster: confirm.label,
            value: rotated.bootstrapToken,
            expiresAt: rotated.expiresAt,
          });
        }
      }
      setConfirm(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setBusyId(null);
    }
  }

  function openCreate() {
    const tenants = accessibleTenants || [];
    const preferred = (selectedTenants || []).find((tenant) => tenants.includes(tenant)) || tenants[0] || '';
    setForm({
      tenant: preferred,
      environment: '',
      clusterName: '',
      profile: 'openshift-readonly',
      clusterClassification: 'production',
    });
    setCreateOpen(true);
  }

  async function createBroker() {
    const tenant = form.tenant.trim();
    const environment = form.environment.trim();
    const clusterName = form.clusterName.trim();
    if (!tenant || !environment || !clusterName) {
      setError('Tenant, environment, and cluster name are required.');
      return;
    }
    if (form.profile === 'developer-admin' && form.clusterClassification !== 'development') {
      setError('developer-admin is only allowed for a development cluster.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const created = await apiClient.createClusterBroker({
        tenant,
        environment,
        clusterName,
        profile: form.profile,
        clusterClassification: form.clusterClassification,
      });
      setCreateOpen(false);
      setToken({
        id: created.id,
        cluster: `${tenant}/${environment}/${clusterName}`,
        value: created.bootstrapToken,
        expiresAt: created.expiresAt,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the broker');
    } finally {
      setCreating(false);
    }
  }

  if (hasNoGroups) return <NoGroupsMessage />;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Cable}
        iconColor="text-sky-600 dark:text-sky-400"
        iconBgColor="bg-sky-100 dark:bg-sky-900/30"
        description="Private cluster brokers enrolled with this portal"
        count={canView ? visible.length : undefined}
        countLabel="brokers"
        countColor="text-sky-600 dark:text-sky-400"
        actions={canView ? (
          <div className="flex items-stretch w-full sm:w-auto h-full">
            <DropdownMenu
              className="h-full w-full"
              trigger={
                <Button variant="outline" className="h-full w-full gap-2">
                  <Settings className="w-4 h-4" />
                  Actions
                </Button>
              }
              items={[
                ...(canManage ? [{
                  label: 'Create broker',
                  icon: <Plus className="w-4 h-4" />,
                  onClick: openCreate,
                }] : []),
                {
                  label: 'Refresh',
                  icon: <RefreshCw className="w-4 h-4" />,
                  onClick: () => void load(),
                },
              ]}
            />
          </div>
        ) : undefined}
      />

      {error ? (
        <Card><CardContent className="p-4 text-sm text-red-600 dark:text-red-400">{error}</CardContent></Card>
      ) : null}

      {permissionLoading || (loading && rows.length === 0) ? (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((item) => (
            <Card key={item}>
              <CardHeader>
                <Skeleton className="h-5 w-40 mb-2" />
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !canView ? (
        <Card><CardContent className="py-8 text-center text-sm text-gray-600 dark:text-gray-300">You don't have access to cluster brokers.</CardContent></Card>
      ) : error && rows.length === 0 ? null : visible.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">No cluster brokers are registered for the selected tenants.</CardContent></Card>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 overflow-visible">
          {visible.map((row) => {
            const kubernetesVersion = row.metadata?.kubernetesVersion;
            return (
              <Card key={row.id} className="hover:shadow-lg transition-shadow w-full max-w-full overflow-visible">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center flex-shrink-0">
                        <Cable className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base truncate">{row.clusterName}</CardTitle>
                        <CardDescription className="mt-0.5 text-xs truncate">
                          {row.tenant} · {row.environment}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Badge variant={statusVariant(row.status)} className="text-xs">{statusLabel(row.status)}</Badge>
                      {canManage && row.status !== 'revoked' ? (
                        <DropdownMenu
                          align="end"
                          trigger={
                            <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Broker actions">
                              <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </button>
                          }
                          items={[
                            {
                              label: 'Rotate identity',
                              onClick: () => {
                                if (busyId === row.id) return;
                                setConfirm({ id: row.id, action: 'rotate', label: `${row.tenant}/${row.clusterName}` });
                              },
                            },
                            {
                              label: 'Remove',
                              className: 'text-red-600 dark:text-red-400',
                              onClick: () => {
                                if (busyId === row.id) return;
                                setConfirm({ id: row.id, action: 'revoke', label: `${row.tenant}/${row.clusterName}` });
                              },
                            },
                          ]}
                        />
                      ) : null}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 pb-3">
                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center gap-3 pt-1 flex-wrap">
                      <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                        <Shield className="w-3 h-3 text-sky-500" />
                        <span>{profileLabel(row.profile)}</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                        <Tag className="w-3 h-3 text-amber-500" />
                        <span>{classificationLabel(row.clusterClassification)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                        <Package className="w-3 h-3 text-blue-500" />
                        <span className="font-medium">{row.imageVersion || 'Unknown image'}</span>
                      </div>
                      {kubernetesVersion ? (
                        <span className="text-gray-500 dark:text-gray-400">Kubernetes {kubernetesVersion}</span>
                      ) : null}
                    </div>
                    {row.lastError ? (
                      <div className="text-red-600 dark:text-red-400">{row.lastError}</div>
                    ) : null}
                    <div className="flex items-center justify-between pt-1.5 border-t border-gray-100 dark:border-gray-800">
                      <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <Clock className="w-3 h-3" />
                        <span>Seen {formatSeen(row.lastSeen)}</span>
                      </div>
                      <span className="text-gray-400 dark:text-gray-500">
                        {row.protocolVersion ? `Protocol ${row.protocolVersion}` : 'No protocol'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent maxWidth="md">
          <DialogHeader>
            <DialogTitle>Create broker</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-3">
              <div>
                {fieldLabel('Tenant')}
                {accessibleTenants.length > 0 ? (
                  <select
                    className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                    value={form.tenant}
                    onChange={(event) => setForm({ ...form, tenant: event.target.value })}
                  >
                    {accessibleTenants.map((tenant) => <option key={tenant} value={tenant}>{tenant}</option>)}
                  </select>
                ) : (
                  <Input value={form.tenant} onChange={(event) => setForm({ ...form, tenant: event.target.value })} />
                )}
              </div>
              <div>
                {fieldLabel('Environment')}
                <Input value={form.environment} onChange={(event) => setForm({ ...form, environment: event.target.value })} placeholder="prod" />
              </div>
              <div>
                {fieldLabel('Cluster name')}
                <Input value={form.clusterName} onChange={(event) => setForm({ ...form, clusterName: event.target.value })} placeholder="demo" />
              </div>
              <div>
                {fieldLabel('Profile')}
                <select
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                  value={form.profile}
                  onChange={(event) => setForm({ ...form, profile: event.target.value })}
                >
                  {PROFILES.map((profile) => <option key={profile} value={profile}>{profile}</option>)}
                </select>
              </div>
              <div>
                {fieldLabel('Classification')}
                <select
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                  value={form.clusterClassification}
                  onChange={(event) => setForm({ ...form, clusterClassification: event.target.value })}
                >
                  <option value="production">production</option>
                  <option value="development">development</option>
                </select>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={() => void createBroker()} disabled={creating}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(open) => { if (!open) setConfirm(null); }}>
        <DialogContent maxWidth="md">
          <DialogHeader>
            <DialogTitle>{confirm?.action === 'revoke' ? 'Remove broker' : 'Rotate broker identity'}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {confirm?.action === 'revoke'
                ? `Remove ${confirm.label}? The registration is deleted, the broker disconnects, and this id cannot enroll again.`
                : `Rotate ${confirm?.label}? The current session closes and a new bootstrap token is issued. The broker must enroll again.`}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant={confirm?.action === 'revoke' ? 'destructive' : 'default'} onClick={() => void runConfirmed()} disabled={!!busyId}>
              {confirm?.action === 'revoke' ? 'Remove' : 'Rotate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!token} onOpenChange={(open) => { if (!open) setToken(null); }}>
        <DialogContent maxWidth="lg">
          <DialogHeader>
            <DialogTitle>{token?.value ? 'Bootstrap token' : 'Broker already registered'}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {token?.value ? (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Registration {token.id} for {token.cluster}. The token expires {formatWhen(token.expiresAt)}. Copy it now and pass it to broker install on the cluster. It is not shown again.
                </p>
                <pre className="text-xs break-all whitespace-pre-wrap rounded-md bg-gray-100 dark:bg-gray-900 p-3">{token.value}</pre>
              </>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {token?.cluster} is already registered as {token?.id}. No new bootstrap token was issued.
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            {token?.value ? (
              <Button
                variant="outline"
                onClick={() => { if (token.value) void navigator.clipboard.writeText(token.value); }}
              >
                Copy token
              </Button>
            ) : null}
            <Button onClick={() => setToken(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
